import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useReferentialContext } from "./Context";

export type PlayerLinkRequest = {
    playerId: string;
    puuid: string;
    accountName?: string;
    onComplete?: (ok: boolean) => void;
};

type QueuedLink = PlayerLinkRequest & { id: number };

type Toast = {
    id: number;
    message: string;
    severity: "success" | "error";
};

type PlayerLinkQueueContextValue = {
    enqueuePlayerLink: (request: PlayerLinkRequest) => boolean;
    isPending: (puuid: string) => boolean;
};

const PlayerLinkQueueContext = createContext<PlayerLinkQueueContextValue | undefined>(undefined);

function accountLabel(request: PlayerLinkRequest) {
    return request.accountName?.trim() || "account";
}

export function PlayerLinkQueueProvider(props: { children: ReactNode }) {
    const { reckonApi } = useReferentialContext();
    const apiRef = useRef(reckonApi);
    apiRef.current = reckonApi;

    const queueRef = useRef<QueuedLink[]>([]);
    const runningRef = useRef(false);
    const idRef = useRef(0);
    const toastQueueRef = useRef<Toast[]>([]);
    const toastRef = useRef<Toast | null>(null);
    const [pendingPuuids, setPendingPuuids] = useState<string[]>([]);
    const [toast, setToast] = useState<Toast | null>(null);

    const syncPending = () => {
        setPendingPuuids(queueRef.current.map((job) => job.puuid));
    };

    const showNextToast = (next: Toast | null) => {
        toastRef.current = next;
        setToast(next);
    };

    const enqueueToast = (message: string, severity: Toast["severity"]) => {
        const next: Toast = { id: ++idRef.current, message, severity };
        if (!toastRef.current) {
            showNextToast(next);
            return;
        }
        toastQueueRef.current.push(next);
    };

    const closeToast = (id?: number) => {
        if (id != null && toastRef.current?.id !== id) return;
        showNextToast(toastQueueRef.current.shift() ?? null);
    };

    const drainRef = useRef<() => Promise<void>>(async () => undefined);
    drainRef.current = async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        while (queueRef.current.length) {
            const job = queueRef.current[0];
            const label = accountLabel(job);
            let ok = false;
            try {
                await apiRef.current.player.add_account({
                    id: job.playerId,
                    add_account: { puuid: job.puuid },
                });
                ok = true;
                enqueueToast(`Linked ${label} to ${job.playerId}`, "success");
            } catch {
                enqueueToast(`Could not link ${label} to ${job.playerId}`, "error");
            }
            queueRef.current.shift();
            syncPending();
            job.onComplete?.(ok);
        }
        runningRef.current = false;
    };

    const enqueuePlayerLink = useCallback((request: PlayerLinkRequest) => {
        if (!request.playerId || !request.puuid) return false;
        if (queueRef.current.some((job) => job.puuid === request.puuid)) return false;
        queueRef.current.push({ ...request, id: ++idRef.current });
        syncPending();
        void drainRef.current();
        return true;
    }, []);

    const isPending = useCallback(
        (puuid: string) => pendingPuuids.includes(puuid),
        [pendingPuuids],
    );

    return (
        <PlayerLinkQueueContext.Provider value={{ enqueuePlayerLink, isPending }}>
            {props.children}
            <Snackbar
                key={toast?.id}
                open={Boolean(toast)}
                autoHideDuration={5000}
                onClose={(_, reason) => {
                    if (reason === "clickaway") return;
                    closeToast(toast?.id);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{ zIndex: 2000 }}
            >
                {toast ? (
                    <Alert onClose={() => closeToast(toast.id)} severity={toast.severity} variant="filled">
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </PlayerLinkQueueContext.Provider>
    );
}

export function usePlayerLinkQueue() {
    const context = useContext(PlayerLinkQueueContext);
    if (!context) {
        throw new Error("usePlayerLinkQueue must be used within a PlayerLinkQueueProvider");
    }
    return context;
}
